import type { ImageEvaluation } from "@/lib/api/agents"
import type { QEVerdict } from "./types"

// Quick Estimate 3 — derive a single user-facing verdict from the
// AI evaluation result.
//
// QE2 has a similar helper (./derive-verdict.ts) that also factors
// in expected-side mismatch — QE3 has no expected side (free-form)
// so we only score quality. Order of checks matters: blurry > too-
// dark > too-close > low-quality, because a single image can fail
// multiple ways and we want to surface the most actionable one.

const ISSUE_KEYWORDS = {
  blurry: ["blur", "motion", "shake"],
  tooDark: ["dark", "underexposed", "low light", "shadow"],
  tooClose: ["close", "cropped", "zoom", "framing"],
} as const

function matchesAny(issues: string[], keys: readonly string[]): boolean {
  return issues.some((iss) =>
    keys.some((k) => iss.toLowerCase().includes(k)),
  )
}

export function deriveQEVerdict(ev: ImageEvaluation): QEVerdict {
  if (ev.qualified && (ev.qualityScore ?? 0) >= 60) return "good"
  const issues = ev.qualityIssues ?? []
  if (matchesAny(issues, ISSUE_KEYWORDS.blurry)) return "blurry"
  if (matchesAny(issues, ISSUE_KEYWORDS.tooDark)) return "too-dark"
  if (matchesAny(issues, ISSUE_KEYWORDS.tooClose)) return "too-close"
  // No specific signal but the AI flagged it — generic low-quality.
  return "low-quality"
}

// Plain-English copy per verdict. Always blames the system, never
// the user. The retake CTA (handled in the overlay component) is
// always primary; the "use anyway" affordance is always secondary.
export const VERDICT_COPY: Record<QEVerdict, { title: string; body: string }> = {
  good: {
    title: "Looking good",
    body: "AI tagged this clean.",
  },
  "too-close": {
    title: "Too close",
    body: "Stand back so the whole damaged area is in frame.",
  },
  "too-dark": {
    title: "Too dark",
    body: "Move into better light or turn on the phone flashlight.",
  },
  blurry: {
    title: "Blurry",
    body: "Hold steady and retake — the AI can't read motion blur.",
  },
  "wrong-side": {
    title: "Wrong side",
    body: "AI thinks this is a different angle. Pick the right side or retake.",
  },
  "low-quality": {
    title: "Hard to make out",
    body: "Retake with the damage centered and well-lit.",
  },
  "eval-failed": {
    title: "Couldn't tag",
    body: "AI didn't respond. Retry, or send without an AI tag.",
  },
}

// Whether the verdict warrants showing the overlay at all. "good"
// is the only verdict that stays out of the user's way.
export function verdictNeedsAttention(v: QEVerdict): boolean {
  return v !== "good"
}
